// Loadout PLP taxonomy — CSV-grounded (2026-08-01).
// Uses `aliases` arrays for tag-form flexibility (bare + prefixed forms)
// and per-facet `normalizeTag` functions to canonicalise tags to a facet
// value.  See src/lib/filters.js for the matcher.

export const COLLECTION_HANDLE_TO_TYPES = {
  all: [],
  keyboards: ["keyboard","keyboards","mechanical keyboard","gaming keyboard","key board","60 keyboard","65 keyboard","75 keyboard","tkl keyboard"],
  mice: ["mouse","mice","gaming mouse","optical mouse","wireless mouse"],
  mousepads: ["mousepad","mousepads","mouse pad","deskpad","desk pad","mouse mat"],
  keycaps: ["keycap","keycaps","keycap set","keycaps set"],
  audio: ["audio","headset","headsets","iem","iems","earphone","earphones","earbud","earbuds","microphone"],
  switches: ["switch","switches","keyboard switch","keyboard switches","mx switch","hall effect switch"],
  accessories: ["accessory","accessories","cable","hub","stand","mount"],
  "pcb-modules": ["pcb","pcb module","pcb modules","pcb-mount","hotswap pcb","hotswap module","hotswap modules"],
};

// Helpers used inside `normalizeTag` — strip common prefixes / suffixes.
const stripPrefix = (t, ...prefixes) => {
  for (const p of prefixes) {
    if (t.startsWith(p)) return t.slice(p.length);
  }
  return t;
};

// Layout tags observed: `65-percent`, `layout:65`, `65%`, `65`. Canonical: `65`.
const normLayout = (t) => {
  const s = stripPrefix(t, "layout:")
    .replace(/-percent$/, "")
    .replace(/%$/, "");
  return s;
};

// Switch tech: bare `he` / `hall-effect`, or `switch:he` / `switch:hall-effect`.
const normSwitchTech = (t) => {
  const s = stripPrefix(t, "switch:");
  if (s === "hall-effect") return "he";
  return s;
};

// Switches sound profile / type — bare or `sound:` / `switch:` prefixed.
const normSwitchesSound = (t) => stripPrefix(t, "sound:");
const normSwitchesType = (t) => {
  const s = stripPrefix(t, "switch:");
  if (s === "hall-effect") return "he";
  return s;
};

// Connectivity: bare `wired`/`wireless` or `connect:wired`.
const normConnect = (t) => {
  const s = stripPrefix(t, "connect:");
  if (s === "2-4g") return "2.4g";
  if (s === "bt") return "bluetooth";
  return s;
};

// Mouse sensor: bare `paw3950` or `sensor:paw3950`.
const normSensor = (t) => stripPrefix(t, "sensor:");

// Pretty labels for sensor keys — fallback to UPPER if missing.
const SENSOR_LABELS = {
  paw3950: "PAW3950",
  paw3395: "PAW3395",
  paw3955: "PAW3955",
  paw3311: "PAW3311",
  other: "Other",
};

// Mouse polling: `polling:8k` → `8k`.
const normPolling = (t) => stripPrefix(t, "polling:");

// Mouse shape: `shape:symmetric` → `symmetric`. Ambi/right-handed → symmetric-ish;
// keep raw for facet counting so extra rows just show as themselves — we still
// count them into `symmetric` when they equal `ambidextrous`.
const normShape = (t) => {
  const s = stripPrefix(t, "shape:");
  if (s === "ambidextrous") return "symmetric";
  return s;
};

// Mouse size: `size:m` / bare `medium` / `m`.
const normSize = (t) => {
  const s = stripPrefix(t, "size:");
  if (s === "small") return "s";
  if (s === "medium") return "m";
  if (s === "large") return "l";
  return s;
};

// Mouse feature flags — exact bare tags only (`rapidtrigger`, `magnesium`).
const MOUSE_FEATURES = new Set(["rapidtrigger", "magnesium"]);
const normMouseFeature = (t) => (MOUSE_FEATURES.has(t) ? t : null);

// Mouse weight — special: substring match into 4 buckets. GUARDED: the
// normalizer only accepts tags that are explicitly weight tags (either
// `weight:*` prefix OR an exact bare weight term). Without this guard a
// `size:medium` tag would also hit "med" and leak into the Weight facet.
// Real tag examples: `weight:ultralight-30-to-40`, `weight:superlight-40-to-50`,
// `weight:light-50-60`, `weight:med-60-70`, bare `medium`.
// Order matters: `med` is matched BEFORE `light` because `superlight` contains
// the substring `light`; `ultra`/`super` are matched before `med`.
const BARE_WEIGHTS = new Set(["ultralight", "superlight", "light", "medium", "med"]);
const normWeight = (t) => {
  const hasPrefix = t.startsWith("weight:");
  if (!hasPrefix && !BARE_WEIGHTS.has(t)) return null;
  const s = hasPrefix ? t.slice(7) : t;
  if (s.includes("ultralight")) return "ultralight";
  if (s.includes("superlight")) return "superlight";
  if (s.includes("med")) return "medium";
  if (s.includes("light")) return "light";
  return null;
};

// Mousepad glide/surface: bare token, `glide:speed`, `surface:cloth`.
const normGlide = (t) => stripPrefix(t, "glide:");
const normSurface = (t) => stripPrefix(t, "surface:");

// Keycap material: bare `pbt` / `pc`, or `material:pbt`.
const normMaterial = (t) => stripPrefix(t, "material:");

// Audio type: bare `iem` / `headphones` / `dac`, or `audiotype:iem`/`audiotype:headset`.
const normAudioType = (t) => {
  const s = stripPrefix(t, "audiotype:");
  if (s === "headset") return "headphones";
  return s;
};

const ACCESSORY_ALIASES = {
  hub: "hub", hubs: "hub", cable: "cables", cables: "cables",
  "grip-tape": "grip-tapes", "grip-tapes": "grip-tapes", griptape: "grip-tapes", griptapes: "grip-tapes",
  "mouse-skates": "mouse-skates", skates: "mouse-skates", sleeve: "sleeves", sleeves: "sleeves",
  case: "cases", cases: "cases", storage: "storage", misc: "misc",
};
const normAccessory = (t) => ACCESSORY_ALIASES[t] || null;

// PCB layout — reuse layout normalizer.
const normPcbLayout = normLayout;

const TAXONOMY = {
  keyboards: {
    brand:        { label: "Brand",        source: "brand" },
    layout:       { label: "Layout",       source: "tag", normalize: normLayout,
                    values: ["60","65","75","80","tkl","full"] },
    switch_type:  { label: "Switch Tech",  source: "tag", normalize: normSwitchTech,
                    values: ["he"] },
    connectivity: { label: "Connectivity", source: "tag", normalize: normConnect,
                    values: ["wired","wireless"] },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  mice: {
    brand:        { label: "Brand",        source: "brand" },
    sensor:       { label: "Sensor",       source: "tag", normalize: normSensor,
                    values: ["paw3950","paw3395","paw3955","paw3311","other"],
                    labels: SENSOR_LABELS },
    weight:       { label: "Weight",       source: "tag", normalize: normWeight,
                    values: ["ultralight","superlight","light","medium"],
                    labels: {
                      ultralight: "Ultralight (<40g)",
                      superlight: "Superlight (40-50g)",
                      light: "Light (50-60g)",
                      medium: "Medium (60g+)",
                    } },
    polling:      { label: "Polling Rate", source: "tag", normalize: normPolling,
                    values: ["1k","4k","8k"] },
    shape:        { label: "Shape",        source: "tag", normalize: normShape,
                    values: ["symmetric","ergonomic"] },
    size:         { label: "Size",         source: "tag", normalize: normSize,
                    values: ["fingertip","s","m","l"],
                    labels: { fingertip: "Fingertip", s: "S", m: "M", l: "L" } },
    features:     { label: "Features",     source: "tag", normalize: normMouseFeature,
                    values: ["rapidtrigger","magnesium"],
                    labels: { rapidtrigger: "Rapid Trigger", magnesium: "Magnesium" } },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  mousepads: {
    brand:        { label: "Brand",        source: "brand" },
    glide:        { label: "Glide",        source: "tag", normalize: normGlide,
                    values: ["speed","control","balanced"] },
    surface:      { label: "Surface",      source: "tag", normalize: normSurface,
                    values: ["cloth","glass"] },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  keycaps: {
    brand:        { label: "Brand",        source: "brand" },
    material:     { label: "Material",     source: "tag", normalize: normMaterial,
                    values: ["pbt","pc"] },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  switches: {
    brand:        { label: "Brand",        source: "brand" },
    switch_type:  { label: "Switch Type",  source: "tag", normalize: normSwitchesType,
                    values: ["he","mx"] },
    sound:        { label: "Sound Profile", source: "tag", normalize: normSwitchesSound,
                    values: ["creamy","marbly","buttery","thocky","clacky","muted"] },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  audio: {
    brand:        { label: "Brand",        source: "brand" },
    type:         { label: "Type",         source: "tag", normalize: normAudioType,
                    values: ["iem","tws","headphones","dac"],
                    labels: { iem: "IEM", tws: "TWS", headphones: "Headphones", dac: "DAC" } },
    connectivity: { label: "Connectivity", source: "tag", normalize: normConnect,
                    values: ["wired","wireless"] },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  "pcb-modules": {
    brand:        { label: "Brand",        source: "brand" },
    layout:       { label: "Layout",       source: "tag", normalize: normPcbLayout,
                    values: ["60","65","75","tkl","full"] },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  accessories: {
    brand:        { label: "Brand",        source: "brand" },
    type:         { label: "Type",         source: "tag", normalize: normAccessory,
                    values: ["hub","cables","grip-tapes","mouse-skates","sleeves","cases","storage","misc"],
                    labels: { hub: "Hub", cables: "Cables", "grip-tapes": "Grip Tapes", "mouse-skates": "Skates",
                              sleeves: "Sleeves", cases: "Cases", storage: "Storage", misc: "Misc" } },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
  default: {
    brand:        { label: "Brand",        source: "brand" },
    price:        { label: "Price",        type: "price" },
    stock_status: { label: "Availability", type: "stock" },
  },
};

export const COLLECTION_TO_TAXONOMY_KEY = Object.keys(COLLECTION_HANDLE_TO_TYPES).reduce(
  (acc, k) => {
    acc[k] = k === "all" ? "default" : k;
    return acc;
  },
  {},
);

export function getTaxonomyForHandle(handle) {
  const key = COLLECTION_TO_TAXONOMY_KEY[handle] || "default";
  const tax = TAXONOMY[key] || TAXONOMY.default;
  // Availability is always the first facet so shoppers see stock status
  // before anything else.
  const { stock_status, ...rest } = tax;
  return stock_status ? { stock_status, ...rest } : tax;
}

export const PRICE_BOUNDS = { min: 0, max: 50000, step: 500 };
